import { PencilIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Editor from "@via/components/rich-text-editor/rte-editor.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import { useAppsList } from "@via/pages/apps/apps.api.tsx";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link.tsx";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  EMPTY_TABLE_MESSAGE,
  ERROR,
  EditorState,
  ROUTES,
} from "@via/utils/constants.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";
import { createMarkup } from "@via/utils/html-utils.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  AppListResponseBody,
  AppListResponseBodyItem,
  RunStatus,
  type ProjectRunResponse,
} from "common";
import { type JSX, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useProject,
  useProjectRunList,
  useUpdateProject,
} from "@via/pages/projects/projects.api.tsx";
import { useAbility } from "@via/context/ability-context.tsx";
import { Can } from "@casl/react";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const initialRunSuccessStats = {
  success: 0,
  total: 0,
  percentage: 0,
};

const sort: Array<{ id: string; desc: boolean }> = [
  {
    id: "updatedAt",
    desc: true,
  },
];

export default function Dashboard(): JSX.Element {
  const { projectId = "" } = useParams();
  const {
    data,
    isLoading: isProjectLoading,
    isError: isProjectError,
    isSuccess: isProjectSuccess,
    error: projectError,
  } = useProject(projectId);
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: data?.own,
    },
  ]);
  const [isEditing, setIsEditing] = useState(false);
  const [runSuccessStats, setRunSuccessStats] = useState(
    initialRunSuccessStats
  );
  const {
    data: appsListData,
    isLoading: isAppsListLoading,
    isError: isAppsListError,
  } = useAppsList();
  const {
    data: runData,
    isLoading: isRunListLoading,
    isError: isRunListError,
    isSuccess: isRunListSuccess,
  } = useProjectRunList(projectId, 0, 10, sort, [], isProjectSuccess);
  const {
    mutate: updateProject,
    isPending: isUpdateProjectLoading,
    isSuccess: isUpdateProjectSuccess,
  } = useUpdateProject();

  const runsColumnHelper = createColumnHelper<ProjectRunResponse>();
  const appsColumnHelper = createColumnHelper<AppListResponseBodyItem>();
  const runListColumns = [
    runsColumnHelper.accessor("id", {
      size: 100,
      cell: ({ row }) => {
        return (
          <Link to={`/runs/${row.original.id}`} className="via-link truncate">
            {row.original.id}
          </Link>
        );
      },
      header: () => <span>Run ID</span>,
    }),
    runsColumnHelper.accessor("name", {
      size: 200,
      cell: ({ row }) => {
        return (
          <Link to={`/runs/${row.original.id}`} className="via-link truncate">
            <TruncatedText
              text={row.original.name}
              maxLength={15}
              id={`tooltip-${row.original.id}-${row.original.name}`}
            />
          </Link>
        );
      },
      header: () => <span>Run Name</span>,
    }),
    runsColumnHelper.accessor("pipelineName", {
      // A standalone process run's pipelineId is a synthesized one node pipeline, and the
      // pipeline editor refuses to open it, so this cell must not link there. RunPipelineLink
      // owns that rule for all four surfaces that render a run's pipeline reference; do not
      // re-inline a <Link> here. TruncatedText stays as its label so the narrow column keeps
      // truncating and keeps its tooltip.
      cell: ({ row }) => {
        return (
          <RunPipelineLink
            pipelineId={row.original.pipelineId}
            pipelineName={row.original.pipelineName}
            subjectType={row.original.subjectType}
            sourceProcessId={row.original.sourceProcessId}>
            <TruncatedText
              id={`tooltip-${row.original.id}-${row.original.pipelineId}`}
              text={row.original.pipelineName}
              maxLength={15}
            />
          </RunPipelineLink>
        );
      },
      header: () => <span>Pipeline Name</span>,
    }),
    runsColumnHelper.accessor("status", {
      size: 150,
      cell: info => getRunStatusLabel(info.getValue()),
      header: () => <span>Status</span>,
    }),
  ] as Array<ColumnDef<ProjectRunResponse>>;

  const appsListColumns = [
    appsColumnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
    }),
    appsColumnHelper.accessor("dateModified", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Last Modified</span>,
    }),
  ] as Array<ColumnDef<AppListResponseBody>>;

  const updateEditingState = (
    newEditState: EditorState,
    editorData?: string
  ): void => {
    switch (newEditState) {
      case EditorState.Editing:
        setIsEditing(true);
        break;
      case EditorState.Canceled:
        setIsEditing(false);
        break;
      case EditorState.Saved:
        updateProject({ id: projectId, data: { summary: editorData } });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (runData) {
      // Statuses to consider for success rate calculation
      const relevantStatuses = [
        RunStatus.NextSuccess,
        RunStatus.Error,
        RunStatus.NextError,
      ];

      // Filter runs that have status success or error
      const filteredRuns = runData.data.filter((d: ProjectRunResponse) =>
        relevantStatuses.includes(d.status as RunStatus)
      );

      const success = filteredRuns.filter(
        (d: ProjectRunResponse) => d.status === RunStatus.NextSuccess
      ).length;

      const total = filteredRuns.length;

      const percentage =
        total > 0
          ? Math.round(((success / total) * 100 + Number.EPSILON) * 100) / 100
          : 0;

      const stats = {
        success,
        total,
        percentage,
      };
      setRunSuccessStats(stats);
    }
  }, [isRunListSuccess]);

  useEffect(() => {
    if (isUpdateProjectSuccess) {
      setIsEditing(false);
    }
  }, [isUpdateProjectSuccess, setIsEditing]);

  const handleEditClick = (): void => {
    updateEditingState(EditorState.Editing);
  };

  const getProjectDashboardContext = useCallback(() => {
    const recentRuns = (runData?.data || []).map(run => ({
      id: run.id,
      name: run.name,
      pipelineName: run.pipelineName,
      pipelineId: run.pipelineId,
      status: run.status,
      updatedAt: run.updatedAt,
    }));

    const recentApps = (appsListData?.data || []).map(app => ({
      id: app.id,
      name: app.name,
      version: app.version,
      dateModified: app.dateModified,
    }));

    return {
      project: {
        id: data?.id,
        name: data?.name,
        summary: data?.summary,
        own: data?.own,
        owner: data?.owner,
        createdAt: data?.createdAt,
        updatedAt: data?.updatedAt,
      },
      statistics: {
        runSuccessRate: {
          percentage: runSuccessStats.percentage,
          success: runSuccessStats.success,
          total: runSuccessStats.total,
        },
      },
      recentRuns: recentRuns,
      recentApps: recentApps,
    };
  }, [data, runData?.data, appsListData?.data, runSuccessStats]);

  useRegisterCortexContext({
    routePattern: ROUTES.PROJECT_DASHBOARD,
    contextName: "Project Dashboard",
    getContext: getProjectDashboardContext,
    systemPrompt:
      "You are analyzing project dashboard page. Help answer questions about the project, recent runs, recent apps, and run success statistics.",
    enabled: !!data && isProjectSuccess,
  });

  return (
    <main className="py-6">
      <div className="flex flex-row flex-wrap">
        <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-7/12 sm:rounded-lg">
          <div className="flex flex-row items-center justify-between">
            <h2 className="via-h2">Project Description</h2>
            <Can I="View" a="Button" ability={ability}>
              <button
                type="submit"
                className="ml-4 inline-flex items-center justify-center rounded-md px-3 text-sm font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                onClick={() => {
                  handleEditClick();
                }}>
                {isEditing ? (
                  <>
                    <PencilSquareIcon className="h-3 w-3" /> &nbsp;Editing
                  </>
                ) : (
                  <>
                    <PencilIcon className="h-3 w-3" /> &nbsp;Edit Description
                  </>
                )}
              </button>
            </Can>
          </div>
          {isProjectLoading ? (
            <div className="py-6">
              <SkeletonLoader count={1} />
            </div>
          ) : isProjectError ? (
            <div className="via-generic-error">
              <GenericError
                message={
                  (projectError as ErrorNotification).response?.data?.message ||
                  ERROR
                }
              />
            </div>
          ) : isEditing ? (
            <Editor
              updateEditingState={updateEditingState}
              editingData={data?.summary ?? ""}
              loading={isUpdateProjectLoading}
            />
          ) : (
            <div className="ql-snow">
              <div
                className="ql-editor"
                dangerouslySetInnerHTML={createMarkup(
                  data?.summary
                    ? data?.summary
                    : `<p class="text-sm text-gray-500">You have no Description at this time. Click <span class="font-semibold">"Edit Description"</span> to toggle into "Edit" mode.</p>`
                )}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col flex-wrap pl-4 sm:basis-6/12 md:basis-5/12">
          <div className="mb-6 flex flex-col px-6 py-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:rounded-lg">
            <h2 className="via-h2">
              <dt className="text-base font-semibold text-gray-900">
                Run Success Rate
              </dt>
              {isRunListLoading ? (
                <div className="py-6">
                  <SkeletonLoader count={1} />
                </div>
              ) : isRunListError ? (
                <GenericError message={ERROR} />
              ) : runSuccessStats.total === 0 ? (
                <div className="flex flex-col py-2 text-sm font-normal text-gray-500">
                  You do not have any completed runs to report on at this time.
                </div>
              ) : (
                <>
                  <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                    <div
                      className={classNames(
                        runSuccessStats.percentage >= 50
                          ? "text-green-600"
                          : "text-red-400",
                        "flex items-baseline text-2xl font-semibold"
                      )}>
                      {runSuccessStats.percentage}%
                    </div>
                  </dd>
                  <span className="text-xs font-medium text-gray-400">
                    {runSuccessStats.success} run(s) out of the last{" "}
                    {runSuccessStats.total} completed runs were successfully
                  </span>
                </>
              )}
            </h2>
          </div>
          <div className="mb-6 px-6 py-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:rounded-lg">
            <h2 className="via-h2">
              Recent Runs
              {isRunListLoading ? (
                <div className="py-6">
                  <SkeletonLoader count={1} />
                </div>
              ) : isRunListError ? (
                <div className="flex flex-col items-center text-center">
                  <GenericError message={ERROR} />
                </div>
              ) : (
                <Table
                  data={runData?.data || []}
                  columns={runListColumns}
                  applyColumnSize
                  size={TableSize.Small}
                  showPagination={false}
                  emptyMessage={EMPTY_TABLE_MESSAGE.RUNS}
                />
              )}
            </h2>
          </div>
          <div className="mb-6 px-6 py-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:rounded-lg">
            <h2 className="via-h2">
              Recent Apps
              {isAppsListLoading ? (
                <div className="py-6">
                  <SkeletonLoader count={1} />
                </div>
              ) : isAppsListError ? (
                <div className="flex flex-col items-center text-center">
                  <GenericError message={ERROR} />
                </div>
              ) : (
                <Table
                  data={appsListData}
                  columns={appsListColumns}
                  size={TableSize.Small}
                  showPagination={false}
                  sort={sort}
                  emptyMessage={EMPTY_TABLE_MESSAGE.APPS}
                />
              )}
            </h2>
          </div>
        </div>
      </div>
    </main>
  );
}
